import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpEventType, HttpRequest, HttpResponse } from '@angular/common/http';
import { File as phxFile, IFileList } from '../models/file';
import { BaseRestService } from './base-rest.service';
import { of, Subject as rxSubject } from 'rxjs';
import { Observable } from 'rxjs';
import { IParentEntity } from 'app/shared/interfaces/entity';
import { HttpErrorResponse } from '@angular/common/http';
import { ListSortOrderItem } from './listSortAndFilterHelpers';
import { DefaultDataService } from './default-data.service';
import { catchError, debounceTime, map, take } from 'rxjs/operators';
import { KeyValuePair } from '../models/keyvaluepair';
import { EventService } from './event.service';
import { AutoUnsubscribe } from 'auto-unsubscribe.decorator';

export interface Dictionary<T> {
  [Key: string]: T;
}
@AutoUnsubscribe()
@Injectable({ providedIn: 'root' })
export class FileService extends BaseRestService<phxFile, IFileList> implements OnDestroy {

  // public maxFileUploadSizeMb = 1024;
  // public maxFileUploadLimit = 1024;
  public maxFileImportSizeMb = 100;

  parent: IParentEntity;

  currentSortOrder: ListSortOrderItem;

  debouncer: rxSubject<any> = new rxSubject();

  constructor(http: HttpClient,
    defaultDataService: DefaultDataService,
    private eventService: EventService,
    protected router: Router,
  ) {
    super(FileService, http, 'api/files', 'file', router);

    // enable caching
    this.cacheEnabled = false;
    // this.maxFileUploadLimit = defaultDataService.maxFileUploadLimit;
    // this.maxFileUploadSizeMb = defaultDataService.maxFileUploadSizeMb;
    this.maxFileImportSizeMb = defaultDataService.maxFileUploadSizeMb;

    this.debouncer
      .pipe(debounceTime(1000))
      .subscribe(() => {
        this.getFiles();
      });
  }

  ngOnDestroy(): void {

  }

  extensionAllowed(filename: string): boolean {
    const name = filename.toLowerCase();
    return !(name.endsWith('.exe') ||
      name.endsWith('.dll') ||
      name.endsWith('.ocx') ||
      name.endsWith('.bat') ||
      name.endsWith('.cmd') ||
      name.endsWith('.msi') ||
      name.endsWith('.ps') ||
      name.endsWith('.psl'));
  }

  setParent(parent?: IParentEntity) {
    this.parent = parent;
  }

  getFiles() {
    this.getFilesFor(this.parent);
  }

  getExistingFiles(includeProperties: boolean = false, bForceRefresh: boolean = false): Observable<phxFile[]> {
    var path = btoa('\\' + this.parent.type + '\\' + this.parent.uid);
    if (includeProperties === true)
      path = path + '/true/true';
    return this.getAllData(path, '', bForceRefresh);
  }

  public getFilesFor(parent: IParentEntity) {
    this.getAll(btoa('\\' + parent.type + '\\' + parent.uid) + '/true/true', '');
  }

  public getAll(type?: string, urlParams?: string) {
    this.getAllData(type, urlParams).pipe(take(1)).subscribe((data) => {
      this.dataLoading(false);
      this.onChanged.next(this.applyDataFilter(data));
    }, (error) => { this.dataLoading(false); this.onChanged.next([]); });
  }

  copyFiles(source: IParentEntity, destination: IParentEntity) {
    return this.http.get<string>(`api/copyfiles/${btoa('\\' + source.type + '\\' + source.uid)}/${btoa('\\' + destination.type + '\\' + destination.uid)}`).pipe(
      map((data) => {
        return data;
      }),
      catchError(this.handleError<string>(`Error copying files`, true)),
    );
  }

  public uploadFiles(files: any[]): Observable<any> {
    if (files.length === 0) {
      return;
    }

    const formData = new FormData();
    for (const file of files) {
      // console.log('Adding file to upload : ', file);
      formData.append(file.name, file);

      const uploadInfo: UploadInfo = new UploadInfo();
      uploadInfo.fileName = file.name;
      uploadInfo.lastModified = new Date(file.lastModified || file.lastModifiedDate).getTime();
      uploadInfo.fileSize = file.size;
      uploadInfo.parent = this.parent;
      uploadInfo.labels = file.labels;
      uploadInfo.summary = file.summary;
      formData.append(file.name + '.info', JSON.stringify(uploadInfo));
    }

    return this.http
      .post('api/files', formData, { reportProgress: true })
      .pipe(catchError((err: HttpErrorResponse) => {
        let error = '';
        if (err.error instanceof Error) {
          // A client-side or network error occurred. Handle it accordingly.
          error = err.error.message;
          console.error('An error occurred:', error);
        }
        else {
          // The backend returned an unsuccessful response code.
          error = err.error;
          console.error(`\nBackend returned code ${err.status}, body was: ${err.error}`);
        }
        return of(`\nError uploading files for ${this.parent.summary}: ${error}`);
      }));
  }

  public upload(files: Set<File>, fileSummaryList: Dictionary<string>): { [key: string]: Observable<number> } {
    // this will be the our resulting map
    const status = {};

    files.forEach((file: any) => {
      // create a new multipart-form for every file
      const formData: FormData = new FormData();
      formData.append(file.name, file);

      const uploadInfo: UploadInfo = new UploadInfo();
      uploadInfo.fileName = file.name;
      uploadInfo.lastModified = new Date(file.lastModified || file.lastModifiedDate).getTime();
      uploadInfo.fileSize = file.size;
      uploadInfo.parent = this.parent;
      uploadInfo.summary = fileSummaryList[file.name] || '';

      uploadInfo.labels = '';
      formData.append(file.name + '.info', JSON.stringify(uploadInfo));

      // create a http-post request and pass the form
      // tell it to report the upload progress
      const req = new HttpRequest('POST', 'api/files', formData, {
        reportProgress: true,
      });

      // create a new progress-subject for every file
      const progress = new rxSubject<number>();
      const progressErrors = new rxSubject<string>();

      // send the http-request and subscribe for progress-updates
      const startTime = new Date().getTime();
      this.http.request(req).subscribe((event) => {
        if (event.type === HttpEventType.UploadProgress) {

          // calculate the progress percentage
          const percentDone = Math.round((100 * event.loaded) / event.total);

          // pass the percentage into the progress-stream
          progress.next(percentDone);
          progressErrors.next('');

        } else if (event instanceof HttpResponse) {
          // Close the progress-stream if we get an answer form the API
          // The upload is complete
          progress.next(100);
          progress.complete();

          progressErrors.next('');
          progressErrors.complete();
        }
      }, (error) => {
        progress.next(100);
        progress.complete();

        console.log(error);
        progressErrors.next(error ? error.toString() : '');
        progressErrors.complete();
      });

      // Save every progress-observable in a map of all observables
      status[file.name] = {
        progress: progress.asObservable(),
        progressErrors: progressErrors.asObservable(),
      };
    });

    // refresh task types which may have been updated to reflect the addition of the file(s)
    this.eventService.fileUploaded();

    // return the map of progress.observables
    return status;
  }

  getDownloadLink(uri: string): Observable<string> {
    return this.http.get<string>(`/api/getDownloadLink/${btoa(uri)}`);
  }

  getViewOnlineLink(uri: string): Observable<string> {
    return this.http.get<string>(`/api/getViewLink/${btoa(uri)}`);
  }

  deleteFile(file: phxFile) {
    return this.http.delete<string>(`/api/files/${btoa(file.uri)}`).subscribe((x) => {
      this.eventService.fileDeleted();
      this.debouncer.next(undefined);
    });
  }

  updateMetadata(file: phxFile, metadata: KeyValuePair[]) {
    this.http.put<string>(`/api/files/${btoa(file.uri)}`, JSON.stringify(metadata)).pipe(take(1),
      catchError(this.handleError<string>(`Failed to update file summary and labels.`, true))).subscribe((x) => {
      // do nothing - ui already updated
    }, (e)=>{
      console.error("Failed to update file summary and label data",e);
    }
    );
  }

  getSpecialFileUrl(uriPart:string, errorMsg:string):Observable<string>{
    return this.http.get<string>('/api/'+uriPart).pipe(
      take(1),
      map((response) => {
        return response;
      }),
      catchError(this.handleError<string>(errorMsg, true)),
    );    
  }

  getMailMergeDataFileDownloadLink(): Observable<string> {
    return this.getSpecialFileUrl('getMailMergeDataFileUri',`Get Mail Merge Data File`);    
  }

  getMailMergeMappingFileDownloadLink(): Observable<string> {
    return this.getSpecialFileUrl('getMailMergeMappingFileUri',`Get Mail Merge Mapping File`);        
  }

  getFakeDataSettingsFileUri():Observable<string>{
    return this.getSpecialFileUrl('getFakeDataSettingsFileUri',`Get Fake Data Settings File`);
  }
  

  public applySortOrder(files: phxFile[], sortOrder?: ListSortOrderItem): phxFile[] {

    // stash the sort order for future use
    this.currentSortOrder = sortOrder;

    const ab: number = sortOrder.order;
    const ba: number = sortOrder.order * -1;

    switch (sortOrder.title) {
      default:
      case 'Date': return files.sort((a, b) => {
        const x = (a.lastModifiedDate) ? new Date(a.lastModifiedDate).getTime() : 0;
        const y = (b.lastModifiedDate) ? new Date(b.lastModifiedDate).getTime() : 0;
        return ((x < y) ? ba : ((x > y) ? ab : 0));
      });
      case 'Size': return files.sort((a, b) => {
        const x = a.fileSize;
        const y = b.fileSize;
        return ((x < y) ? ba : ((x > y) ? ab : 0));
      });
      case 'Filename': return files.sort((a, b) => {
        const x = a.fileName.toLowerCase();
        const y = b.fileName.toLowerCase();
        return ((x < y) ? ba : ((x > y) ? ab : 0));
      });
    }
  }
}

// tslint:disable-next-line:max-classes-per-file
class UploadInfo {
  fileName: string;
  fileSize: number;
  lastModified: number;
  parent: IParentEntity;
  labels: string;
  summary: string;
}
